import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { Button } from 'reactstrap';
import { CoreOneRosterConfigurationCombinedTeam, OneRosterCombinedTeamFlowSyncType, CoreOneRosterConfigurationCombinedTeamSource } from '../../../../../../models/CoreOneRosterConfigurationCombinedTeam';
import { GetCombinedTeams } from '../../../../../../store/OneRoster/OneRosterCombineTeamAction';
import OneRosterCombineTeamGroupsEdit from './OneRosterCombineTeamGroupsEdit';
import TableHorizontal, { InfoObject } from '../../../../../misc/TableHorizontal';
import { PageResult } from '../../../../../../models/CoreModels';
import { CacheRequestState } from '../../../../../../models/RequestState';
import StatusCode from '../../../../../../util/StatusCode';
import * as Helper from '../../../../../../util/Helper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OneRosterCombineTeamCreateNew from './OneRosterCombineTeamCreateNew';
import OneRosterCombineTeamActivitiesEdit from './OneRosterCombineTeamActivitiesEdit';
import OneRosterCombineTeamDelete from './OneRosterCombineTeamDelete';
import FilterBottomComponent from '../../../../../misc/FilterBottomComponent';
import { useTranslation } from 'react-i18next';
import RequestParser from '../../../../../../util/RequestParser';
import { FlowSyncAccessPolicy, isAuthorized } from '../../../../../../models/FlowSyncAccessPolicy';
import { RootState } from '../../../../../../store/store';


export enum ActionType {
    NONE = 'NONE',
    NEW = 'NEW',
    EDIT = 'EDIT',
    DELETE = 'DELETE'
};

const OneRosterCombineTeam = () => {
    const { moduleId } = useParams();
    const dispatch = useDispatch();
    const { t } = useTranslation(['configuration']);

    const [action, setAction] = useState<ActionType>(ActionType.NONE);
    const [team, setTeam] = useState<CoreOneRosterConfigurationCombinedTeam | undefined>(undefined);
    const combinedTeams = useSelector<RootState, CacheRequestState<PageResult<CoreOneRosterConfigurationCombinedTeam>>>(state => state.onerosterCombineTeam.combinedTeams);
    const authorizedAccessPolicies = useSelector<RootState, FlowSyncAccessPolicy[]>(state => state.me.me?.authorizedAccessPolicies ?? []);

    const [searchParams, setSearchParams] = useSearchParams();
    const filter = RequestParser.parsePageRequest(searchParams);

    const disabled = !isAuthorized(authorizedAccessPolicies, FlowSyncAccessPolicy.EDIT_INFORMATION)

    useEffect(() => {
        if (moduleId) {
            GetCombinedTeams(moduleId, filter)(dispatch);
        }
    }, [dispatch, moduleId, searchParams]);

    const updateTable = (success: boolean) => {
        if (success && moduleId) {
            GetCombinedTeams(moduleId, { page: 1 })(dispatch);
            window.scrollTo(0, 0);
        }
    }

    const clearFilter = () => {
        setSearchParams([]);
    }

    const updateFilter = (name: string, value?: number | string) => {
        if (value !== undefined && value !== '' && value !== null) {
            if (typeof (value) === 'string') {
                searchParams.set(name, value);
            } else {
                searchParams.set(name, value.toString());
            }
        } else {
            searchParams.delete(name);
        }

        setSearchParams(searchParams);
    }

    const renderEditModal = () => {
        if (team === undefined) {
            return;
        }
        if (team.flowSyncType === OneRosterCombinedTeamFlowSyncType.Group)
            return (
                <OneRosterCombineTeamGroupsEdit
                    isOpen={true}
                    editTeam={team}
                    toggleModal={() => setAction(ActionType.NONE)}
                    success={(success) => updateTable(success)}
                    disabled={disabled}
                />
            );
        if (team.flowSyncType === OneRosterCombinedTeamFlowSyncType.Activity) {
            return (
                <OneRosterCombineTeamActivitiesEdit
                    isOpen={true}
                    editTeam={team}
                    toggleModal={() => setAction(ActionType.NONE)}
                    success={(success) => updateTable(success)}
                    disabled={disabled}
                />
            )
        }
    }

    const renderDeleteModal = () => {
        if (team === undefined) {
            return;
        }
        return (
            <OneRosterCombineTeamDelete
                isOpen={true}
                team={team}
                toggleModal={() => setAction(ActionType.NONE)}
                success={(success) => updateTable(success)}
                disabled={disabled}
            />
        )
    }

    const linkToTeam = (x: CoreOneRosterConfigurationCombinedTeamSource): React.ReactNode => {
        if (x.flowSyncType === OneRosterCombinedTeamFlowSyncType.Group) {
            return (<Link key={x.id} to={`/group/${x.flowSyncGroupId}`}>{x.flowSyncGroupTitle} </Link>)
        }
        if (x.flowSyncType === OneRosterCombinedTeamFlowSyncType.Activity) {
            return (<Link key={x.id} to={`/activity/${x.flowSyncActivityId}`}>{x.flowSyncActivityTitle} </Link>)
        }
        return null;
    }

    const mapCombinedTeams = (): InfoObject[] => {
        if (combinedTeams.code === StatusCode.COMPLETE || (combinedTeams.code === StatusCode.PENDING && combinedTeams?.data && combinedTeams.data?.values !== undefined)) {
            if (combinedTeams.data === undefined || (combinedTeams.data && combinedTeams.data.values === undefined)) {
                return [];
            }

            return combinedTeams.data.values.map((x, i) => ({
                id: i.toString(),
                data: [
                    { value: x.displayName },
                    { value: x.courseTitle },
                    { value: t(`oneRosterConfiguration.combineTeam.flowSyncType.${x.flowSyncType}`, { ns: 'configuration' }) },
                    { value: linkToTeam(x) },
                    {
                        value: x.sources.map(y => linkToTeam(y))
                    },
                    { value: x.active && Helper.active(x.active) },
                    { value: <Button className="btn-icon" disabled={disabled} onClick={() => { setAction(ActionType.EDIT); setTeam(x) }}><FontAwesomeIcon icon="pen" title={t('oneRosterConfiguration.combineTeam.button.edit', { ns: 'configuration' })} /></Button> },
                    { value: <Button className="btn-icon" disabled={disabled} onClick={() => { setAction(ActionType.DELETE); setTeam(x); }}><FontAwesomeIcon icon="trash" title={t('oneRosterConfiguration.combineTeam.button.delete', { ns: 'configuration' })} /></Button> },
                ]
            }))
        } else {
            return [];
        }
    }

    const renderTable = () => {
        return (
            <TableHorizontal
                header={[
                    { value: t('oneRosterConfiguration.combineTeam.table.displayName', { ns: 'configuration' }) },
                    { value: t('oneRosterConfiguration.combineTeam.table.courseTitle', { ns: 'configuration' }) },
                    { value: t('oneRosterConfiguration.combineTeam.table.type', { ns: 'configuration' }) },
                    { value: t('oneRosterConfiguration.combineTeam.table.owner', { ns: 'configuration' }) },
                    { value: t('oneRosterConfiguration.combineTeam.table.sources', { ns: 'configuration' }) },
                    { value: t('oneRosterConfiguration.combineTeam.table.active', { ns: 'configuration' }) },
                    { value: "", className: "w20px" },
                    { value: "", className: "w20px" }
                ]}
                info={mapCombinedTeams()}
                error={combinedTeams.code === StatusCode.ERROR}
                searching={combinedTeams.code === StatusCode.PENDING}
                displayName={t('oneRosterConfiguration.combineTeam.title', { ns: 'configuration' })}
                noResMsg={t('oneRosterConfiguration.combineTeam.noResMsg', { ns: 'configuration' })}
            />
        )
    }

    return (
        <div className="mt-4">
                <h5>{t('oneRosterConfiguration.combineTeam.title', { ns: 'configuration' })}</h5>
                <Button disabled={disabled} color="primary" className="mb-4 mt-3" onClick={() => { setAction(ActionType.NEW) }}>{t('oneRosterConfiguration.combineTeam.button.createNew', { ns: 'configuration' })}</Button>
                {renderTable()}


            <div style={{marginLeft: "-40px", marginRight: "-40px"}}>
            <FilterBottomComponent
                requestState={combinedTeams}
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
            />
            </div>

            {action === ActionType.NEW &&
                <OneRosterCombineTeamCreateNew
                    isOpen={true}
                    success={(success) => updateTable(success)}
                    toggleModal={() => setAction(ActionType.NONE)}
                    disabled={disabled}
                />}

            {action === ActionType.EDIT &&
                renderEditModal()}

            {action === ActionType.DELETE &&
                renderDeleteModal()}

        </div>);
}

export default OneRosterCombineTeam;
